import { EncryptedArtifactRef } from '@viko/protocol'
import {
  ArtifactCard,
  type ArtifactItem,
  ArtifactThumb,
  type ArtifactVariant,
  artifactItems,
  type TranscriptView,
  type ViewerContextValue,
} from '@viko/transcript-ui'
import { useMemo } from 'react'
import { EncryptedArtifact } from '../components/EncryptedArtifact.js'
import { EncryptedSnapshot } from '../components/EncryptedSnapshot.js' // auto-artifacts
import type { BrowserArtifacts } from './encrypted-artifacts.js'

export interface ArtifactViewerAccess {
  /** Account from the trusted session, never an artifact response. */
  accountId: string
  required: boolean
  reader?: BrowserArtifacts | undefined
}

export function EndpointArtifact({
  artifact,
  access,
  variant = 'thumb',
}: {
  artifact: ArtifactItem
  access?: ArtifactViewerAccess | undefined
  variant?: ArtifactVariant | undefined
}) {
  const card = variant === 'card'
  const encrypted = !!(
    access?.required ||
    artifact.encrypted !== undefined ||
    artifact.thumbEncrypted !== undefined
  )
  if (!encrypted)
    return card ? (
      <ArtifactCard artifact={artifact} state={artifact.state} />
    ) : (
      <ArtifactThumb artifact={artifact} state={artifact.state} />
    )
  const reference = EncryptedArtifactRef.safeParse(artifact.encrypted)
  const thumb =
    artifact.thumbEncrypted !== undefined
      ? EncryptedArtifactRef.safeParse(artifact.thumbEncrypted)
      : undefined
  if (
    !access?.reader ||
    !reference.success ||
    reference.data.accountId !== access.accountId ||
    reference.data.objectId !== artifact.artifactId ||
    (thumb &&
      (!thumb.success ||
        thumb.data.accountId !== access.accountId ||
        thumb.data.objectId !== artifact.thumbArtifactId))
  )
    return card ? (
      <ArtifactCard artifact={artifact} state={artifact.state} locked />
    ) : (
      <span role="status">Attachment locked or unavailable</span>
    )
  if (card)
    // The card mounts the endpoint renderer only while its preview is open (lazy decryption).
    return (
      <ArtifactCard
        artifact={artifact}
        state={artifact.state}
        preview={
          // auto-artifacts: sealed page snapshots render in the sandboxed snapshot frame.
          artifact.bundle && artifact.mime === 'text/html' ? (
            <EncryptedSnapshot
              reader={access.reader}
              reference={reference.data}
              title={artifact.external?.title ?? artifact.name}
            />
          ) : (
            <EncryptedArtifact
              reader={access.reader}
              reference={reference.data}
              {...(thumb?.success ? { thumbnailReference: thumb.data } : {})}
              textPreview
            />
          )
        }
      />
    )
  return (
    <figure className="vk-art vk-art--encrypted">
      <EncryptedArtifact
        reader={access.reader}
        reference={reference.data}
        {...(thumb?.success ? { thumbnailReference: thumb.data } : {})}
      />
      <figcaption className="vk-art__cap">
        {artifact.caption ?? artifact.name}
        {artifact.path ? <span className="vk-meta"> · {artifact.path}</span> : null}
      </figcaption>
    </figure>
  )
}

/** No key lookups in the reducer. Every rendered URL is created/released by the endpoint component. */
export function useArtifactViewer(
  access: ArtifactViewerAccess | undefined,
  view: TranscriptView,
): Partial<ViewerContextValue> {
  // Artifact items change only with the artifact evidence index (including reset/run changes).
  // biome-ignore lint/correctness/useExhaustiveDependencies: avoid a full nested walk for each text delta
  const byId = useMemo(
    () => new Map(artifactItems(view).map((a) => [a.artifactId, a])),
    [view.runId, view.progress.artifacts],
  )
  return useMemo(
    () => ({
      encryptedArtifacts: access?.required ?? false,
      artifactById: (id: string) => byId.get(id),
      renderArtifact: (artifact: ArtifactItem, opts?: { variant?: ArtifactVariant }) => (
        <EndpointArtifact artifact={artifact} access={access} variant={opts?.variant} />
      ),
    }),
    [access, byId],
  )
}
